import { FilterOperator, IFacetGroupCount, IFilter, IFilterQueryParams, ISorter } from '@clientShared/filter.types';
import { element } from '@shared/easyfy.core';

export class FilterQueryParamsHelper {
  static getQueryParams(
    page: number,
    sorter: ISorter,
    useHashedUrlFragments: boolean,
    isPageLoad: boolean,
    filterQuerySelector: string,
    facetGroupCount: IFacetGroupCount = null
  ): IFilterQueryParams {
    let queryParams: IFilterQueryParams = null;
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    if (useHashedUrlFragments && isPageLoad) {
      queryParams = {
        checkedFilters: this.getFilterDataFromHashFragments(filterQuerySelector),
        page: page,
        sorter: {
          key: this.getSortKeyFromHashFragments(),
          sortDirection: this.getSortDirectionFromHashFragment(),
        },
        searchString: searchQuery,
      };
    } else {
      queryParams = {
        checkedFilters: this.getFilterData(filterQuerySelector),
        page: page,
        sorter: {
          key: sorter?.key ?? null,
          sortDirection: Number.parseInt(SortDirection[sorter?.sortDirection]),
        },
        searchString: searchQuery,
      };
    }

    if (queryParams?.checkedFilters) {
      queryParams.checkedFilters.forEach((filter) => {
        filter.key = decodeURIComponent(filter.key);

        for (let i = 0; i < filter.values.length; i++) {
          filter.values[i] = decodeURIComponent(filter.values[i].replace(/\+/g, ' '));
        }
      });
    }

    if (facetGroupCount) {
      queryParams.applyFilterGroupCount = facetGroupCount;
    }

    if (queryParams.sorter.key == null || queryParams.sorter.sortDirection == null) {
      queryParams.sorter = null;
    }
    return queryParams;
  }

  static setQueryParams = (queryParams: IFilterQueryParams) => {
    const filterUrlQuery = new URLSearchParams();
    const pagingUrlQuery = new URLSearchParams(window.location.search);

    queryParams.page > 1 ? pagingUrlQuery.set('p', queryParams.page.toString()) : pagingUrlQuery.delete('p');

    if (queryParams?.checkedFilters?.length > 0) {
      const filterQuery = queryParams.checkedFilters.map((filter) => `${filter.key}:${filter.values.join('|')};`).join('');
      filterUrlQuery.set('f', filterQuery);
    } else {
      filterUrlQuery.delete('f');
    }

    if (queryParams.sorter != null) {
      filterUrlQuery.set('s', queryParams.sorter.key);
      filterUrlQuery.set('d', queryParams.sorter.sortDirection.toString());
    } else {
      filterUrlQuery.delete('s');
      filterUrlQuery.delete('d');
    }

    const url = `${window.location.origin}${window.location.pathname}`;
    const pagingQueryString = pagingUrlQuery.toString();
    const filterQueryString = filterUrlQuery.toString();

    history.replaceState(
      null,
      null,
      pagingQueryString.length > 0 || filterQueryString.length > 0
        ? decodeURIComponent(
            `${url}${pagingQueryString.length > 0 ? `?${pagingQueryString}` : ''}${filterQueryString.length > 0 ? `#${filterQueryString}` : ''}`
          )
        : url
    );
  };

  //#region Private Methods
  private static getSortKeyFromHashFragments = () => {
    if (!window.location.hash) return null;

    const sortKey = this.getFragmentFromHash(window.location.hash.substring(1), 's');
    return sortKey == null || sortKey == '' ? null : sortKey;
  };

  private static getSortDirectionFromHashFragment = () => {
    if (!window.location.hash) return null;

    const sortDir = Number.parseInt(this.getFragmentFromHash(window.location.hash.substring(1), 'd'));
    return isNaN(sortDir) ? 1 : sortDir;
  };

  private static getFilterDataFromHashFragments = (filterQuerySelector: string): IFilter[] => {
    const filterContainer = element(filterQuerySelector);
    if (!filterContainer) return;

    const filters: IFilter[] = [];

    if (!window.location.hash) {
      return filters;
    }

    const hashFragment = window.location.hash.substring(1);

    const filterFragment = this.getFragmentFromHash(hashFragment, 'f');

    let parts = filterFragment.split(';');

    parts = parts.filter((v) => v != '');

    for (const part of parts) {
      const key = part.split(':')[0];
      const values = part.split(':')[1].split('|');

      const filter: IFilter = {
        key: key,
        operator: FilterOperator.Equal,
        values: values,
      };

      filters.push(filter);
    }

    return filters;
  };

  private static getFragmentFromHash = (hashFragment: string, part: string) => {
    const fragments = hashFragment.split(/(?=&[^&]+=)/);

    let filterFragment = '';
    for (let frag of fragments) {
      if (frag.charAt(0) === '&') {
        frag = frag.slice(1);
      }

      if (frag.includes(part + '=')) {
        filterFragment = frag;
      }
    }

    return filterFragment.substring(2);
  };

  private static getFilterData = (filterQuerySelector): IFilter[] => {
    const filterContainer = element(filterQuerySelector);
    if (!filterContainer) return;

    const filters: IFilter[] = [];
    filterContainer.querySelectorAll('[data-container="filter-group"]').forEach((filterGroupElement: HTMLElement) => {
      const key = filterGroupElement.attributes['data-key']?.value;

      const filter: IFilter = {
        key: key,
        operator: FilterOperator.Equal,
        values: [],
      };

      (filterGroupElement.querySelectorAll('[data-action="change-filter"]:checked') as NodeListOf<HTMLInputElement>)?.forEach(
        (filterInputElement: HTMLInputElement) => {
          filter.values.push(filterInputElement.value);
        }
      );

      if (filter.values.length > 0) {
        filters.push(filter);
      }
    });

    return filters;
  };
  //#endregion
}

enum SortDirection {
  'Ascending' = 0,
  'Descending' = 1,
}
